import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_BASE_URL, apiUrl, BaseUrls } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { AllowedDevice } from '@app/core/interfaces/allowed-device';
import { CallCenter } from '@app/core/interfaces/c5/acd/call-center';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { QueueAgent } from '@app/core/interfaces/c5/acd/queue-agent';
import { C5Enterprise } from '@app/core/interfaces/c5/c5-enterprise';
import { DeviceSpecification } from '@app/core/interfaces/c5/device-specification';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { LocationGroup } from '@app/core/interfaces/c5/location-group';
import { PhoneBookContact } from '@app/core/interfaces/c5/phone-book-contact';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { RoutingGroup } from '@app/core/interfaces/c5/routing-group';
import { User } from '@app/core/interfaces/c5/user';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { Enterprise } from '@app/core/interfaces/enterprise';
import { EnterpriseLocal } from '@app/core/interfaces/enterprise-local';
import { EnterpriseOption } from '@app/core/interfaces/enterprise-option';
import { AssignedFeatureProfile, FeatureProfile } from '@app/core/interfaces/feature-profile';
import { ScreenNumber } from '@app/core/interfaces/screen-number';
import { SearchEnterprisePhoneNumbersParameters } from '@app/core/interfaces/search-enterprise-phone-numbers-parameters';
import { Site } from '@app/core/interfaces/site';
import { SiteCommon } from '@app/core/interfaces/site-common';
import { SiteLocal } from '@app/core/interfaces/site-local';
import { TrunkCommon } from '@app/core/interfaces/trunk-common';
import { Registration } from '@app/core/models/registration';
import { isServiceProvider, UserLevel } from '@app/core/models/user-level';
import { RouterService } from '@app/routing/router.service';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { EnhancedEndUserLite } from '@app/shared/models/enhanced-end-user-lite';
import { EvaluatedNumber } from '@app/shared/models/evaluated-number';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { Identifier } from '@app/utils/common';
import { CustomURLEncoder, toHttpParams } from '@app/utils/http';
import { wrapError } from '@app/utils/rxjs';
import { flatMapWithCache } from '@app/utils/simple-cache';
import { enhanceUsersWithExtension } from '@app/utils/user';
import { RouteConfig } from '@business/acd/route-config/models/route-config';
import { EndUserLite } from '@business/end-user/models/end-user-lite';
import { Features } from '@business/feature/interfaces/features';
import { Ivr } from '@business/ivr/models/ivr';
import { TimeTableModel } from '@business/time-table/models/time-table-model';
import { Observable, of } from 'rxjs';
import { first, flatMap, map, mergeMap } from 'rxjs/operators';
import { License } from '../models/license';
import { ApiUrlParams, Utils } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  constructor(
    private authenticationService: AuthenticationService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private http: HttpClient,
    private routerService: RouterService,
    private enterpriseContextService: EnterpriseContextService,
    private utils: Utils
  ) {}

  private emptyCallCenter: CallCenter = {
    name: undefined,
    distribution: undefined,
    supervisorReportRestrict: false,
    queues: [],
    agents: [],
  };

  enterprise(enterpriseId?: Identifier): Observable<Enterprise> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId`,
      [UserLevel.EnterpriseAdmin.toString()]: '/enterprise',
    };

    return this.utils
      .apiUrl(url, { enterpriseId }, !isServiceProvider(this.authenticationService.authUser))
      .pipe(flatMapWithCache((url) => this.http.get<Enterprise>(url)));
  }

  /**
   * Will fetch the enterprise by name over the special '/redir' path from backend.
   * If "hydra" system distribution is employed then it will set the 'hydra-system' cookie to the value where this enterprise resides.
   * Consequently, checking if the application should be reload can be performed afterwards.
   * @param enterpriseName
   */
  enterpriseByNameOverRedirPath(enterpriseName: Identifier): Observable<Enterprise> {
    const url = `/middleware/api/redir/enterprise/${enterpriseName}`;
    return this.http.get<Enterprise>(url);
  }

  fetchAcdQueues(): Observable<Queue[]> {
    const urls = {
      [UserLevel.EndUser]: '/user/acd/agent',
      [UserLevel.EnterpriseAdmin]: BaseUrls.ServiceProvider.Enterprise + '/acdQueue',
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/acdQueue',
    };

    let response$: Observable<Queue[]>;

    if (this.authenticationService.userIsEndUser()) {
      response$ = this.http
        .get<QueueAgent[]>(`${API_BASE_URL}/user/acd/agent`)
        .pipe(map((response) => response.map((_) => _.queue)));
    } else {
      response$ = this.utils.apiUrl(urls).pipe(flatMap((url) => this.http.get<Queue[]>(url)));
    }

    return response$.pipe(wrapError('shared: acd queues'));
  }

  // ACD and call center queues
  fetchAllQueues(enterpriseId: Identifier): Observable<Queue[]> {
    const url: string = `${API_BASE_URL}/v1/enterprise/${enterpriseId}/acdQueue`;

    return this.http.get<Queue[]>(url).pipe(wrapError('shared: acd and cc queues'));
  }

  fetchAllowedDevices(): Observable<AllowedDevice[]> {
    return this.utils.apiUrl('/allowedDevices').pipe(flatMap((url) => this.http.get<AllowedDevice[]>(url)));
  }

  fetchAssignedFeatureProfiles(refresh?: boolean): Observable<AssignedFeatureProfile[]> {
    return this.utils.commonApiUrl('/featureProfile').pipe(
      flatMapWithCache((url) => this.http.get<AssignedFeatureProfile[]>(url), {
        refresh,
      }),
      wrapError('shared: assigned feature profiles')
    );
  }

  fetchUserFeatureProfile(): Observable<FeatureProfile[]> {
    return this.utils.apiUrl('/featureProfile').pipe(
      flatMap((url) => this.http.get<FeatureProfile[]>(url)),
      wrapError('shared: user feature profile')
    );
  }

  fetchCallCenter(): Observable<CallCenter | QueueAgent[]> {
    // if there is no license we simply return empty call-center instance without consulting the back-end
    // this mitigates the problem for already implemented components that expect to get call-center data (as if they are always there).
    return this.enterpriseAuthorizationService.hasEnterpriseLicense$(License.CallCenter).pipe(
      first(),
      flatMap((hasCallCenterLicense) => {
        if (hasCallCenterLicense) {
          const urls = {
            [UserLevel.EndUser]: '/user/callCenter/agent',
            [UserLevel.EnterpriseAdmin]: BaseUrls.ServiceProvider.Enterprise + '/callCenter',
            [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/callCenter',
          };

          return this.utils.apiUrl(urls).pipe(flatMapWithCache((url) => this.http.get<CallCenter>(url)));
        } else {
          if (this.authenticationService.userIsEndUser()) {
            return of([]);
          } else {
            return of(this.emptyCallCenter);
          }
        }
      }),
      wrapError('shared: call center')
    );
  }

  fetchCallCenterEndUserAgents(): Observable<QueueAgent[]> {
    return this.http
      .get<QueueAgent[]>(apiUrl('/user/callCenter/agent'))
      .pipe(wrapError('shared: call center end-user agents'));
  }

  fetchC5Enterprise(): Observable<C5Enterprise> {
    const urls = {
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/c5',
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/c5',
    };

    return this.utils.apiUrl(urls).pipe(
      flatMap((url) => this.http.get<C5Enterprise>(url)),
      wrapError('shared: c5 enterprise')
    );
  }

  fetchC5EnterpriseEndUsers(): Observable<EnhancedEndUser[]> {
    return this.utils.apiUrl('/c5EndUsers').pipe(
      flatMap((url) => this.http.get<EndUserWithId[]>(url)),
      enhanceUsersWithExtension(),
      wrapError('shared: c5 enterprise end users')
    );
  }

  fetchDeviceSpecifications(phoneType?: string): Observable<DeviceSpecification[]> {
    const params: any = {};

    if (phoneType !== undefined) {
      params.deviceSpecType = phoneType;
    }

    return of(apiUrl('/deviceSpecs')).pipe(
      flatMap((url) =>
        this.http.get<DeviceSpecification[]>(url, {
          params,
        })
      ),
      wrapError('shared: device specifications')
    );
  }

  fetchEndUsers(): Observable<EnhancedEndUserLite[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/endusers`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + `/endusers`,
    };

    return this.utils.apiUrl(urls).pipe(
      flatMap((url) => this.http.get<EndUserLite[]>(url)),
      map((users) =>
        users.map((user) => ({
          ...user,
          extension: user.loginName.split('@')[0],
        }))
      ),
      wrapError('shared: end users')
    );
  }

  endUser(enterpriseId?: string | number, endUser?: string | number): Observable<EndUserData> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/users/:userId?id=:userId`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.EndUser + `?id=:userId`,
      [UserLevel.EndUser.toString()]: BaseUrls.EndUser + '/ownData',
    };

    const params = {
      enterpriseId: enterpriseId,
      userId: endUser,
    };

    return this.utils.apiUrl(url, params).pipe(
      flatMap((url) => this.http.get<EndUserData>(url)),
      wrapError('shared: end user')
    );
  }

  fetchEnterprises(): Observable<EnterpriseLocal[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.ServiceProvider + '/enterprises/list',
    };

    return this.utils.apiUrl(urls).pipe(
      flatMap((url) => this.http.get<EnterpriseLocal[]>(url)),
      wrapError('shared: enterprises')
    );
  }

  fetchEnterpriseOptions(urlParams?: ApiUrlParams): Observable<EnterpriseOption[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/option',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/option',
      [UserLevel.EndUser]: BaseUrls.EndUser + '/option',
    };

    return this.utils.apiUrl(urls, urlParams).pipe(
      mergeMap((url) => this.http.get<EnterpriseOption[]>(url)),
      wrapError('shared: enterprise options')
    );
  }

  fetchFeatures(): Observable<Features> {
    return this.utils.apiUrl('/features').pipe(mergeMap((url) => this.http.get<Features>(url)));
  }

  fetchIvrs(enterpriseId: Identifier): Observable<Ivr[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/ivr',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/ivr',
    };

    return this.utils.apiUrl(urls, { enterpriseId }).pipe(
      flatMap((url) => this.http.get<Ivr[]>(url)),
      wrapError('shared: ivrs')
    );
  }

  fetchLocationGroups(siteName: Identifier): Observable<LocationGroup[]> {
    return this.utils.apiUrl(`/sites/${siteName}/locationGroups`).pipe(
      mergeMap((url) => this.http.get<LocationGroup[]>(url)),
      wrapError('shared: location groups')
    );
  }

  fetchPhoneBook(): Observable<PhoneBookContact[]> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/phonebook',
        [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/phonebook',
        [UserLevel.EndUser]: BaseUrls.EndUser + '/phonebook',
      })
      .pipe(
        flatMap((url) => this.http.get<PhoneBookContact[]>(url)),
        wrapError('shared: phone book')
      );
  }

  fetchRegistrations(): Observable<Registration[]> {
    return this.utils.apiUrl('/registrations').pipe(flatMap((url) => this.http.get<Registration[]>(url)));
  }

  /**
   * Fetch route configs for an acd queue.
   *
   *  @param queueId
   */
  fetchRouteConfigs(queueId: Identifier): Observable<RouteConfig[]>;

  /**
   * Fetch route configs for a call center queue.
   *
   * @param callCenterId
   * @param queueId
   */
  fetchRouteConfigs(callCenterId: Identifier, queueId: Identifier): Observable<RouteConfig[]>;

  fetchRouteConfigs(callCenterOrQueueId: Identifier, queueId?: Identifier): Observable<RouteConfig[]> {
    let url$: Observable<string>;

    if (this.authenticationService.userIsEndUser()) {
      url$ = of(API_BASE_URL + `/user/callCenter/${callCenterOrQueueId}/queue/${queueId}/routeConfigs`);
    } else {
      if (queueId != null) {
        url$ = this.utils.apiUrl(`/callCenter/${callCenterOrQueueId}/queue/${queueId}/routeConfigs`);
      } else {
        url$ = this.utils.apiUrl(`/acdQueue/${callCenterOrQueueId}/routeConfigs`);
      }
    }

    return url$.pipe(
      flatMap((url) => this.http.get<RouteConfig[]>(url)),
      wrapError('shared: route configs')
    );
  }

  fetchScreenNumbers(): Observable<ScreenNumber[]> {
    const url = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/screenNumbers',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/screenNumbers',
      [UserLevel.EndUser]: BaseUrls.EndUser + '/screenNumbers',
    };

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.get<ScreenNumber[]>(url)),
      wrapError('shared: screen numbers')
    );
  }

  fetchSite(idOrName: string): Observable<Site> {
    return this.utils.apiUrl(`/sites/${idOrName}`).pipe(
      flatMap((url) => this.http.get<Site>(url)),
      wrapError('shared: site')
    );
  }

  fetchSites(): Observable<Site[]> {
    return this.utils.apiUrl('/sites').pipe(
      flatMap((url) => this.http.get<Site[]>(url)),
      wrapError('shared: sites')
    );
  }

  fetchSitesLocal(): Observable<SiteLocal[]> {
    return this.utils.apiUrl('/sitesLocal').pipe(
      flatMap((url) => this.http.get<SiteLocal[]>(url)),
      wrapError('shared: sites local')
    );
  }

  // for now used only for end-user
  fetchSiteCommon(): Observable<SiteCommon> {
    return this.utils.apiUrl(`/siteCommon`).pipe(
      flatMap((url) => this.http.get<SiteCommon>(url)),
      wrapError('shared: site common')
    );
  }

  fetchSiteEndUsers(siteId: Identifier): Observable<EnhancedEndUser[]> {
    return this.utils.apiUrl(`/sites/${siteId}/endUsers`).pipe(
      flatMap((url) => this.http.get<EndUserWithId[]>(url)),
      enhanceUsersWithExtension(),
      wrapError('shared: site end users')
    );
  }

  fetchSubscriberPhoneNumbers(): Observable<PhoneNumber[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]: BaseUrls.ServiceProvider.Enterprise + '/phoneNumbers',
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + '/phoneNumbers',
      [UserLevel.EndUser.toString()]: BaseUrls.EndUser + '/phoneNumbers',
    };

    return this.utils.apiUrl(urls).pipe(flatMapWithCache((url) => this.http.get<PhoneNumber[]>(url)));
  }

  fetchTimeTablesForUser(userId: number): Observable<TimeTableModel[]> {
    return this.utils
      .apiUrl(`/timeTables?userId=${userId}`)
      .pipe(flatMap((url) => this.http.get<TimeTableModel[]>(url)));
  }

  fetchTimeTablesForSite(siteId: number): Observable<TimeTableModel[]> {
    return this.utils
      .apiUrl(`/timeTables?siteIdOrName=${siteId}`)
      .pipe(flatMap((url) => this.http.get<TimeTableModel[]>(url)));
  }

  fetchTimeTables(id?: string): Observable<TimeTableModel[]> {
    let url: string;

    // End-User level
    if (this.routerService.extractParameters().urlParameters['userId'] != null) {
      url = '/timeTable';
    } else {
      url = '/timeTables';
    }

    if (id != null) {
      url += `/${id}`;
    }

    return this.utils.apiUrl(url).pipe(
      flatMap((url) => this.http.get<TimeTableModel[]>(url)),
      wrapError('shared: time tables')
    );
  }

  searchEnterprisePhoneNumbers(payload: SearchEnterprisePhoneNumbersParameters): Observable<PhoneNumber[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/phoneNumbers/search',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/phoneNumbers/search',
    };

    if (payload.internal != null) {
      payload = {
        ...payload,
        numberType: 'INTERNAL',
      };
    }

    return this.utils.apiUrl(urls).pipe(
      flatMap((url) =>
        this.http.get<PhoneNumber[]>(url, {
          params: toHttpParams(payload),
        })
      ),
      wrapError('shared: enterprise phone numbers')
    );
  }

  getBusinessTrunkUser(userId: number): Observable<User> {
    return this.utils.commonApiUrl(`/btusers/${userId}`).pipe(
      flatMap((url) => this.http.get<User>(url)),
      wrapError('shared: business trunk user')
    );
  }

  fetchRoutingGroups(siteId: number): Observable<RoutingGroup[]> {
    const url = `/sites/${siteId}/routingGroups`;

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.get<RoutingGroup[]>(url)),
      wrapError('shared: routing groups')
    );
  }

  getBusinessTrunk(trunkId: string): Observable<TrunkCommon> {
    const url = `/trunks/${trunkId}`;

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.get<TrunkCommon>(url)),
      wrapError('shared: business trunk')
    );
  }

  listBusinessTrunks(includeTeamsTrunk: boolean = false): Observable<TrunkCommon[]> {
    const url = '/trunks';

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
    };

    let params = new HttpParams();

    if (includeTeamsTrunk === false) {
      params = params.set('teams', 'false');
    }

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) =>
        this.http.get<TrunkCommon[]>(url, {
          params,
        })
      ),
      wrapError('shared: business trunks')
    );
  }

  evaluateE164(e164: string): Observable<EvaluatedNumber> {
    const params = new HttpParams({
      encoder: CustomURLEncoder,
    }).set('e164', e164);

    return this.http.get<EvaluatedNumber>(`${API_BASE_URL}/phoneNumbers/evaluate`, {
      params,
    });
  }
}
